import React, { useState } from 'react'
import Confetti from './Confetti'

const VictoryScreen = ({ lastMatchResult, onStartChat, onReturnToPurgatory }) => {
  const [showResults, setShowResults] = useState(true)

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="card-retro p-8 text-center card-glow">
          <div className="space-y-6">
            <Confetti />
            {/* Escape Celebration */}
            <div className="text-center mb-8">
              <div className="text-8xl mb-4 animate-bounce">🎉</div>
              <h1 className="text-5xl font-bold text-neon-green mb-4 animate-pulse">
                YOU HAVE SUCCESSFULLY ESCAPED PURGATORY
              </h1>
              <div className="text-6xl font-bold text-neon-blue mb-2">
                {(() => {
                  const totalMatches = lastMatchResult.game_summary?.total_matches || lastMatchResult.total_matches || lastMatchResult.matches || 0;
                  const totalQuestions = lastMatchResult.game_summary?.total_questions || 9; // Victory means 3 rounds completed
                  return Math.round((totalMatches / totalQuestions) * 100);
                })()}%
              </div>
              <p className="text-purgatory-mist text-lg">Common Ground (Total)</p>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <button 
                onClick={onStartChat}
                className="btn-primary text-xl py-6 bg-gradient-to-r from-neon-blue to-purple-600 text-white border-2 border-neon-blue hover:from-neon-blue hover:to-purple-700 shadow-lg transform hover:scale-105 transition-all"
              >
                💬 CHAT
              </button>
              <button 
                onClick={onReturnToPurgatory}
                className="btn-secondary text-xl py-6 border-2 border-purgatory-gold text-purgatory-gold hover:bg-purgatory-gold hover:text-purgatory-dark"
              >
                🏠 RETURN
              </button>
            </div>

            {/* Your Results - Collapsible Section */}
            <div className="card-retro p-6">
              <button
                onClick={() => setShowResults(!showResults)}
                className="w-full text-left flex items-center justify-between"
              >
                <h3 className="text-purgatory-gold font-bold text-lg">
                  Your Results
                </h3>
                <div className="text-purgatory-gold text-xl transform transition-transform duration-200">
                  {showResults ? '−' : '+'}
                </div>
              </button>
              
              {showResults && (
                <div className="mt-4 space-y-4">
                  {/* Round Summaries - Show all rounds */}
                  {lastMatchResult.game_summary && lastMatchResult.game_summary.rounds ? (
                    lastMatchResult.game_summary.rounds.map((round, roundIndex) => (
                      <div key={roundIndex} className="mb-6">
                        <div className="text-lg font-bold text-neon-green mb-3">
                          Round {roundIndex + 1} - {Math.round((round.matches_count / 3) * 100)}% Match
                        </div>
                        <div className="space-y-3">
                          {round.questions && round.questions.map((question, qIndex) => (
                            <div key={qIndex} className="bg-purgatory-ash bg-opacity-20 p-3 rounded">
                              <div className="text-sm text-purgatory-mist mb-2">
                                Question {qIndex + 1}
                              </div>
                              <div className="text-sm mb-2">{question.text}</div>
                              <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                  <span className="text-purgatory-mist">You: </span>
                                  <span className={`font-bold ${
                                    round.match_details && round.match_details[qIndex] ? 'text-neon-green' : 'text-purgatory-ember'
                                  }`}>
                                    {question.options && round.user1_answers && question.options[round.user1_answers[qIndex]]}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-purgatory-mist">Them: </span>
                                  <span className={`font-bold ${
                                    round.match_details && round.match_details[qIndex] ? 'text-neon-green' : 'text-purgatory-ember'
                                  }`}>
                                    {question.options && round.user2_answers && question.options[round.user2_answers[qIndex]]}
                                  </span>
                                </div>
                              </div>
                              <div className="text-center mt-2">
                                {round.match_details && round.match_details[qIndex] ? (
                                  <span className="text-neon-green text-xs">✓ MATCH</span>
                                ) : (
                                  <span className="text-purgatory-ember text-xs">✗ NO MATCH</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    /* Fallback - show all rounds from stored data */
                    <div className="space-y-6">
                      {Array.from({ length: lastMatchResult.rounds_completed || 3 }, (_, roundIndex) => (
                        <div key={roundIndex} className="mb-6">
                          <div className="text-lg font-bold text-neon-green mb-3">
                            Round {roundIndex + 1} - {roundIndex === (lastMatchResult.rounds_completed || 3) - 1 
                              ? Math.round(((lastMatchResult.matches || 0) / 3) * 100) 
                              : '100'}% Match
                          </div>
                          <div className="space-y-3">
                            {(lastMatchResult.questions || []).map((question, qIndex) => (
                              <div key={qIndex} className="bg-purgatory-ash bg-opacity-20 p-3 rounded">
                                <div className="text-sm text-purgatory-mist mb-2">
                                  Question {qIndex + 1}
                                </div>
                                <div className="text-sm mb-2">{question.text}</div>
                                <div className="grid grid-cols-2 gap-4 text-xs">
                                  <div>
                                    <span className="text-purgatory-mist">You: </span>
                                    <span className={`font-bold ${
                                      (lastMatchResult.match_details && lastMatchResult.match_details[qIndex]) ? 'text-neon-green' : 'text-purgatory-ember'
                                    }`}>
                                      {(lastMatchResult.player1_answers && question.options && question.options[lastMatchResult.player1_answers[qIndex]]) || 'No answer'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-purgatory-mist">Them: </span>
                                    <span className={`font-bold ${
                                      (lastMatchResult.match_details && lastMatchResult.match_details[qIndex]) ? 'text-neon-green' : 'text-purgatory-ember'
                                    }`}>
                                      {(lastMatchResult.player2_answers && question.options && question.options[lastMatchResult.player2_answers[qIndex]]) || 'No answer'}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-center mt-2">
                                  {(lastMatchResult.match_details && lastMatchResult.match_details[qIndex]) ? (
                                    <span className="text-neon-green text-xs">✓ MATCH</span>
                                  ) : (
                                    <span className="text-purgatory-ember text-xs">✗ NO MATCH</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Donate Link */}
            <div className="text-center mt-8">
              <a 
                href="https://checkout.revolut.com/pay/4b87e61e-9c30-43a3-8822-9877bed4fe24" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purgatory-mist text-sm hover:text-purgatory-gold transition-colors underline"
              >
                Donate to support our work
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VictoryScreen
